import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { getExamples } from "@/lib/api";
import { useLang } from "@/lib/lang-context";

/**
 * Marketing carousel of pre-analysed example reports rendered below the
 * hero. The point is conversion: a visitor without a listing on hand can
 * still see what the product produces, click through to a full report,
 * and decide whether to paste their own URL.
 *
 * Data flow
 * ---------
 * - `GET /api/examples?limit=3` returns ONE analysis per status bucket
 *   (BARGAIN / FAIR / OVERPRICED) so the gallery always shows the
 *   spectrum: a great deal, an honest one, and a trap.
 * - Each card links to the existing `/sheet/:id` permalink that already
 *   renders the full Evaluation Sheet.
 *
 * Auto-hides on fetch error or empty result so a fresh DB doesn't show
 * an awkward "no examples yet" block to first-time visitors.
 */
const STATUS_META = {
  BARGAIN: { Icon: TrendingDown, tone: "bg-[#4A5D23] text-white", border: "border-[#4A5D23]" },
  FAIR: { Icon: Minus, tone: "bg-[#F2EFE6] text-[#282C2A] border border-[#E6E4DD]", border: "border-[#282C2A]" },
  OVERPRICED: { Icon: TrendingUp, tone: "bg-[#FBEBE5] text-[#A0522D] border border-[#F5C6B5]", border: "border-[#A0522D]" },
};

const fmtEur = (n) =>
  !n
    ? "—"
    : new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(n);

const ExamplesGallery = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getExamples(3);
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  if (!items.length) return null;

  return (
    <section
      data-testid="examples-gallery"
      className="max-w-7xl mx-auto px-6 md:px-10 pt-2 pb-16"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 bg-[#4A5D23]" />
            {t("examples.eyebrow")}
          </div>
          <h2 className="font-serif-display text-3xl md:text-4xl mt-2 text-[#282C2A]">
            {t("examples.title")}
          </h2>
        </div>
        <p className="text-xs text-[#6C706D] max-w-sm">{t("examples.sub")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((it, idx) => {
          const meta = STATUS_META[it.status] || STATUS_META.FAIR;
          const Icon = meta.Icon;
          const pricePerSqm =
            it.asking_price && it.surface_sqm
              ? Math.round(it.asking_price / it.surface_sqm)
              : null;
          return (
            <Link
              to={`/sheet/${it.id}`}
              key={it.id}
              data-testid={`example-card-${idx}`}
              className={`group block bg-white border-2 ${meta.border} p-5 hover:shadow-paper transition-shadow`}
            >
              {/* Header row — status + score */}
              <div className="flex items-center justify-between gap-3">
                <span
                  data-testid={`example-status-${idx}`}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${meta.tone}`}
                >
                  <Icon size={11} strokeWidth={1.75} />
                  {t(`examples.status.${it.status?.toLowerCase()}`)}
                </span>
                <div className="text-right">
                  <div className="label-eyebrow">{t("examples.score")}</div>
                  <div
                    data-testid={`example-score-${idx}`}
                    className="font-serif-display text-2xl text-[#282C2A] leading-none mt-1"
                  >
                    {it.total_score ?? "—"}
                    <span className="font-mono text-xs text-[#6C706D] ml-1">/10</span>
                  </div>
                </div>
              </div>

              {/* Town */}
              <div className="flex items-center gap-1.5 mt-5 text-[#6C706D]">
                <MapPin size={12} strokeWidth={1.5} />
                <span
                  data-testid={`example-town-${idx}`}
                  className="text-sm capitalize"
                >
                  {it.town || "—"}
                  {it.province ? ` · ${it.province}` : ""}
                </span>
              </div>

              {/* Headline economic */}
              <p
                data-testid={`example-headline-${idx}`}
                className="mt-3 text-sm text-[#282C2A] line-clamp-3 leading-snug"
              >
                {it.economic_analysis ||
                  (it.title || t("examples.headlineFallback"))}
              </p>

              {/* Price row */}
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#E6E4DD] font-mono text-xs">
                <span className="text-[#282C2A]">
                  {fmtEur(it.asking_price)}{" "}
                  {it.surface_sqm ? (
                    <span className="text-[#6C706D]">· {it.surface_sqm} m²</span>
                  ) : null}
                </span>
                {pricePerSqm ? (
                  <span className="text-[#6C706D]">€{pricePerSqm}/m²</span>
                ) : null}
              </div>

              {/* CTA */}
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#4A5D23] group-hover:gap-2.5 transition-all">
                {t("examples.cta")}
                <ArrowUpRight size={13} strokeWidth={1.75} />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default ExamplesGallery;
